import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { Bell, Search, User, Shield, ChevronDown, LogOut, Settings, Coins, Link2, Mic, MessageSquare, Camera, TrendingUp, Plus, CheckCircle2, Sparkles, FileText, Pill, TestTube, Activity, Calendar, Compass, Eye, Zap, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useAdoption, PHASES } from '../shared/AdoptionJourney';
import { AdoptionLightbox } from '../shared/AdoptionJourney';
import AppSwitcher from '../shared/AppSwitcher';
import TenantSwitcher from '../TenantSwitcher';

import CrossAppNotifications from '../shared/CrossAppNotifications';
import ConnectionStatusIndicator from '../shared/ConnectionStatusIndicator';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';

const PHASE_COLORS = { 1: 'border-white/20 bg-white/10 text-white', 2: 'border-white/20 bg-white/10 text-white', 3: 'border-white/20 bg-white/10 text-white', 4: 'border-white/20 bg-white/10 text-white' };
const PHASE_ICONS = { 1: Eye, 2: Zap, 3: Link2, 4: Layers };

const AdoptionPhaseBadge = ({ onOpen }) => {
  const { phase } = useAdoption();
  const currentPhase = PHASES.find(p => p.id === phase);
  const Icon = PHASE_ICONS[phase] || Eye;
  return (
    <Badge variant="outline" className={cn("text-[10px] gap-1 px-2 py-1 font-medium cursor-pointer hover:opacity-80 transition-opacity", PHASE_COLORS[phase])} data-testid="adoption-phase-badge" onClick={onOpen}>
      <Icon className="h-2.5 w-2.5" /> {currentPhase?.label || 'Assist'}
    </Badge>
  );
};

const CREDIT_PLANS = [
  { id: 1, credits: 50, price: 50, label: 'Starter', popular: false },
  { id: 2, credits: 150, price: 150, label: 'Standard', popular: true },
  { id: 3, credits: 500, price: 500, label: 'Professional', popular: false },
  { id: 4, credits: 1000, price: 1000, label: 'Enterprise', popular: false },
];

const USAGE_BREAKDOWN = [
  { service: 'Ambient Listening', icon: Mic, used: 98, color: 'text-violet-400 bg-violet-500/20' },
  { service: 'Dictation', icon: MessageSquare, used: 34, color: 'text-blue-400 bg-blue-500/20' },
  { service: 'Lumina Lens', icon: Camera, used: 15, color: 'text-amber-400 bg-amber-500/20' },
];

const SEARCH_SUGGESTIONS = [
  { type: 'patient', icon: User, label: 'John Smith', sub: 'DOB: 03/15/1965 · MRN-001 · DM, HTN', href: '/lumina/patients/P001' },
  { type: 'patient', icon: User, label: 'Maria Garcia', sub: 'DOB: 08/22/1978 · MRN-002 · Asthma', href: '/lumina/patients/P002' },
  { type: 'patient', icon: User, label: 'Robert Johnson', sub: 'DOB: 11/30/1980 · MRN-003 · Chronic Pain', href: '/lumina/patients/P003' },
  { type: 'patient', icon: User, label: 'Emily Davis', sub: 'DOB: 04/12/1970 · MRN-004 · HTN, Hypothyroid', href: '/lumina/patients/P004' },
  { type: 'medication', icon: Pill, label: 'Metformin 500mg', sub: 'Active for 3 patients · Interaction alert with contrast', href: '/lumina/patients/P001' },
  { type: 'medication', icon: Pill, label: 'Lisinopril 10mg', sub: 'Active for 5 patients · Monitor potassium', href: '/lumina/patients/P001' },
  { type: 'medication', icon: Pill, label: 'Atorvastatin 20mg', sub: 'Active for 4 patients · Annual LFT due', href: '/lumina/patients/P001' },
  { type: 'lab', icon: TestTube, label: 'HbA1c', sub: '27 patients overdue · Last ordered: Feb 1', href: '/lumina/mips' },
  { type: 'lab', icon: TestTube, label: 'Lipid Panel', sub: '12 patients overdue · Fasting required', href: '/lumina/mips' },
  { type: 'lab', icon: TestTube, label: 'CBC w/ Differential', sub: 'Commonly ordered · No overdue patients', href: '/lumina/mips' },
  { type: 'diagnosis', icon: Activity, label: 'Type 2 Diabetes (E11.9)', sub: '45 active patients · 8 uncontrolled A1c', href: '/lumina/ai-insights' },
  { type: 'diagnosis', icon: Activity, label: 'Essential Hypertension (I10)', sub: '32 active patients · 4 above target', href: '/lumina/ai-insights' },
  { type: 'note', icon: FileText, label: 'John Smith — SOAP Note', sub: 'Feb 3, 2026 · Follow-up · Signed', href: '/lumina/documentation' },
  { type: 'note', icon: FileText, label: 'Maria Garcia — SOAP Note', sub: 'Feb 3, 2026 · New Patient · Draft', href: '/lumina/documentation' },
  { type: 'appointment', icon: Calendar, label: 'Robert Johnson — 10:00 AM', sub: 'Today · Sick Visit · Cough and fever', href: '/lumina/appointments' },
];

const TYPE_LABELS = { patient: 'Patients', medication: 'Medications', lab: 'Labs', diagnosis: 'Diagnoses', note: 'Notes', appointment: 'Appointments' };

const LuminaTopbar = ({ onStartTour, collapsed, role = 'provider', onRoleSwitch }) => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCredits, setShowCredits] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showAdoption, setShowAdoption] = useState(false);
  const searchRef = useRef(null);
  const isMA = role === 'ma';

  // Profile: real user from auth, or demo defaults
  const displayName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Dr. Sarah Chen';
  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : displayName.replace(/^(Dr|Mr|Ms|Mrs|Prof)\.?\s+/i, '').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'SC';

  const totalUsed = USAGE_BREAKDOWN.reduce((s, u) => s + u.used, 0);
  const balance = 247;

  const handlePurchase = (plan) => {
    toast.success(`${plan.credits} credits added to your account`, { description: `Charged $${plan.price}.00` });
    setShowCredits(false);
  };

  const query = searchQuery.toLowerCase().trim();
  const filtered = query.length > 0 ? SEARCH_SUGGESTIONS.filter(s => s.label.toLowerCase().includes(query) || s.sub.toLowerCase().includes(query) || s.type.includes(query)) : [];
  const grouped = filtered.reduce((acc, s) => { if (!acc[s.type]) acc[s.type] = []; acc[s.type].push(s); return acc; }, {});

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchFocused(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item) => { setSearchQuery(''); setSearchFocused(false); navigate(item.href); };
  const showDropdown = searchFocused && (query.length > 0 || searchFocused);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-14 bg-black flex items-center px-5 z-50" data-testid="lumina-topbar">
        {/* Left: App Switcher + Mode + Search */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <AppSwitcher currentApp="lumina" collapsed={false} variant="topbar" />

          {/* Mode Switcher - Segmented Toggle */}
          <div className="flex items-center rounded-[20px] p-0.5 h-7 flex-shrink-0" style={{ background: 'rgba(255,255,255,0.1)' }} data-testid="mode-switcher">
            <button onClick={() => onRoleSwitch?.('provider')}
              className="px-3 h-6 rounded-[18px] text-[12px] transition-all duration-[180ms]"
              style={{
                background: role === 'provider' ? '#ffffff' : 'transparent',
                color: role === 'provider' ? '#000000' : 'rgba(255,255,255,0.45)',
                fontWeight: 500,
              }}
              data-testid="mode-provider">Provider</button>
            <button onClick={() => onRoleSwitch?.('ma')}
              className="px-3 h-6 rounded-[18px] text-[12px] transition-all duration-[180ms]"
              style={{
                background: role === 'ma' ? '#ffffff' : 'transparent',
                color: role === 'ma' ? '#000000' : 'rgba(255,255,255,0.45)',
                fontWeight: 500,
              }}
              data-testid="mode-ma">MA</button>
          </div>

          {isMA && (
            <span className="text-[10px] px-2 py-0.5 rounded flex-shrink-0" style={{
              background: 'rgba(255,255,255,0.08)',
              border: '0.5px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.5)',
              borderRadius: 4,
              letterSpacing: '0.03em',
            }} data-testid="ma-view-badge">MA view</span>
          )}
        </div>

        {/* Center: Search */}
        <div className="flex-1 flex justify-center px-4">
          <div className="relative w-full max-w-md" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
            <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-white/30" />
            <Input type="text" placeholder="Search patients, meds, labs, diagnoses..." className="pl-9 pr-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm h-9 focus-visible:ring-white/20"
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} data-testid="ai-search-input" />

            {showDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden max-h-[420px] overflow-y-auto" data-testid="ai-search-dropdown">
                {query.length === 0 ? (
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-3"><Sparkles className="h-3.5 w-3.5 text-violet-500" /><span className="font-medium">AI-powered search</span></div>
                    <p className="text-xs text-slate-400">Type to search across patients, medications, labs, diagnoses, notes, and appointments</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(isDemo ? ['John Smith', 'Metformin', 'HbA1c', 'Diabetes', "Today's notes"] : ['Metformin', 'HbA1c', 'Diabetes', "Today's notes"]).map((hint, i) => (
                        <button key={i} onClick={() => setSearchQuery(hint)} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-colors" data-testid={`search-hint-${i}`}>{hint}</button>
                      ))}
                    </div>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-4 text-center"><p className="text-sm text-slate-500">No results for "{searchQuery}"</p></div>
                ) : (
                  Object.entries(grouped).map(([type, items]) => (
                    <div key={type}>
                      <div className="px-3 py-2 bg-slate-50 border-b"><p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{TYPE_LABELS[type] || type}</p></div>
                      {items.slice(0, 3).map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <button key={`${type}-${i}`} onClick={() => handleSelect(item)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left" data-testid={`search-result-${type}-${i}`}>
                            <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                              type === 'patient' ? 'bg-slate-100' : type === 'medication' ? 'bg-blue-50' : type === 'lab' ? 'bg-emerald-50' : type === 'diagnosis' ? 'bg-amber-50' : type === 'note' ? 'bg-violet-50' : 'bg-slate-100')}>
                              <Icon className={cn("h-4 w-4", type === 'patient' ? 'text-slate-600' : type === 'medication' ? 'text-blue-600' : type === 'lab' ? 'text-emerald-600' : type === 'diagnosis' ? 'text-amber-600' : type === 'note' ? 'text-violet-600' : 'text-slate-600')} />
                            </div>
                            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{item.label}</p><p className="text-xs text-slate-400 truncate">{item.sub}</p></div>
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="outline" className="gap-1 px-2.5 py-1 text-[10px] font-medium cursor-pointer hover:bg-white/10 border-white/20 text-white transition-colors" data-testid="start-tour-btn" onClick={onStartTour}>
            <Compass className="h-2.5 w-2.5" /> Tour
          </Badge>

          <AdoptionPhaseBadge onOpen={() => setShowAdoption(true)} />

          <Button size="sm" variant="ghost" className="h-8 text-[11px] text-white hover:text-white hover:bg-white/10 gap-1" onClick={() => navigate('/lumina/lens')} data-testid="topbar-lens-btn">
            <Camera className="h-3.5 w-3.5" /> Lens
          </Button>

          <Button size="sm" variant="ghost" className="h-8 text-[11px] text-white hover:text-white hover:bg-white/10 gap-1" data-testid="topbar-assist-btn">
            <Sparkles className="h-3.5 w-3.5" /> Assist
          </Button>

          <div className="h-4 w-px bg-white/15" />

          <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-[11px] font-semibold cursor-pointer hover:bg-white/10 border-amber-500/40 text-amber-400 transition-colors" data-testid="credit-balance-badge" onClick={() => setShowCredits(true)}>
            <Coins className="h-3 w-3" /> {balance} (${balance})
          </Badge>

          <ConnectionStatusIndicator />

          <span className="text-[9px] text-white/40 font-medium" data-testid="hipaa-badge">HIPAA &#10003;</span>
          <TenantSwitcher />

          <div className="h-4 w-px bg-white/15" />

          <CrossAppNotifications currentApp="lumina" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 h-8 hover:bg-white/10" data-testid="user-profile-trigger">
                <div className="h-7 w-7 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold text-white" data-testid="user-initials">{initials}</div>
                <div className="text-left hidden sm:block">
                  <p className="text-xs font-medium text-white" data-testid="user-display-name">{displayName}</p>
                </div>
                <ChevronDown className="h-3 w-3 text-white/50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem><User className="h-4 w-4 mr-2" /> Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/lumina/settings')}><Settings className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={() => { signOut(); navigate('/'); }} data-testid="sign-out-btn"><LogOut className="h-4 w-4 mr-2" /> Sign Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Credits Lightbox */}
      <Dialog open={showCredits} onOpenChange={setShowCredits}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="credits-lightbox">
          <div className="bg-slate-900 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Coins className="h-5 w-5 text-amber-400" /><DialogTitle className="text-lg font-bold text-white">Credits</DialogTitle></div>
              <Badge className="bg-white/15 text-white border-0 text-xs">1 credit = $1.00</Badge>
            </div>
            <div className="flex items-end gap-2">
              <p className="text-4xl font-bold">{balance}</p>
              <p className="text-slate-400 text-sm mb-1">credits (${balance}.00) remaining</p>
            </div>
            <div className="mt-3 h-2 bg-white/15 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full" style={{ width: `${Math.min(100, (balance / (balance + totalUsed)) * 100)}%` }} />
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>{totalUsed} used (${totalUsed}.00)</span><span>{balance} remaining (${balance}.00)</span></div>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Usage Breakdown</p>
              <div className="space-y-2">
                {USAGE_BREAKDOWN.map((item) => { const Icon = item.icon; return (
                  <div key={item.service} className="flex items-center justify-between p-2.5 rounded-lg border">
                    <div className="flex items-center gap-3"><div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", item.color)}><Icon className="h-4 w-4" /></div><span className="text-sm font-medium">{item.service}</span></div>
                    <span className="text-sm font-semibold">{item.used} (${item.used})</span>
                  </div>
                ); })}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Add Credits</p>
              <div className="grid grid-cols-2 gap-2">
                {CREDIT_PLANS.map((plan) => (
                  <button key={plan.id} onClick={() => handlePurchase(plan)}
                    className={cn("relative flex flex-col items-center p-3 rounded-xl border-2 transition-all hover:shadow-md text-center",
                      plan.popular ? "border-violet-500 bg-violet-50/50 hover:bg-violet-50" : "border-slate-200 hover:border-violet-300")} data-testid={`purchase-plan-${plan.id}`}>
                    {plan.popular && <Badge className="absolute -top-2 bg-black text-white text-[9px] px-2 py-0">Most Popular</Badge>}
                    <p className="text-lg font-bold text-slate-900">{plan.credits}</p>
                    <p className="text-[10px] text-muted-foreground">credits</p>
                    <p className="text-sm font-semibold text-violet-700 mt-1">${plan.price}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount Input */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] text-slate-400 font-medium">Or enter a custom amount</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">$</span>
                  <Input type="number" placeholder="0.00" className="pl-7 h-9 text-sm"
                    data-testid="lightbox-custom-amount"
                    onChange={(e) => {
                      const val = e.target.value;
                      const btn = document.querySelector('[data-testid="lightbox-custom-buy-btn"]');
                      const hint = document.querySelector('[data-testid="lightbox-custom-hint"]');
                      if (btn) btn.textContent = val && Number(val) > 0 ? `Add ${Math.round(Number(val))} credits for $${Number(val).toFixed(2)}` : 'Add Credits';
                      if (hint) hint.textContent = val && Number(val) > 0 ? `= ${Math.round(Number(val))} credits · covers approx. ${Math.round(Number(val) / (totalUsed / 30 || 5))} days at your current usage rate` : '';
                    }} />
                </div>
                <p className="text-[11px] text-slate-500" data-testid="lightbox-custom-hint"></p>
                <Button size="sm" className="bg-black hover:bg-gray-800 text-xs w-full" data-testid="lightbox-custom-buy-btn"
                  onClick={() => { toast.success('Credits added'); setShowCredits(false); }}>
                  Add Credits
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-violet-200 bg-violet-50/30">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /><div><p className="text-xs font-semibold text-slate-800">Auto Top-Up</p><p className="text-[10px] text-muted-foreground">Auto-adds 100 credits ($100) when balance drops below 10</p></div></div>
              <Button size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700" onClick={() => toast.success('Auto top-up enabled')} data-testid="enable-auto-topup-btn">Enable</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdoptionLightbox open={showAdoption} onOpenChange={setShowAdoption} />
    </>
  );
};

export default LuminaTopbar;
